import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SelectedComponentsService } from '@iqd/shared-drawing';
import { ErrorModalComponent } from '@iqd/shared-utils';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { first, switchMap } from 'rxjs';
import { InfoModalComponent } from '../info-modal/info-modal.component';
import { LoadingViewComponent } from '../loading-view/loading-view.component';
import { QuoteData } from '../quote-data';
import { CloneQuoteData } from '../quote-data-helper';
import { QuoteSessionService } from '../quote-session.service';

@Component({
  selector: 'hlq-generate-floor-button',
  templateUrl: './generate-floor-button.component.html',
  styleUrls: ['./generate-floor-button.component.scss'],
})
export class GenerateFloorButtonComponent {
  @Input()
  buttonTitle = 'Calculate Grid';
  @Input()
  buttonClass = 'btn btn-primary';
  @Input()
  clearTransforms = false;
  @Input()
  clearCutouts = false;
  @Input()
  hideInfoMessage = false;
  @Output()
  onSuccess = new EventEmitter<any>();
  @Output()
  onErrors = new EventEmitter<string[]>();
  @Output()
  onWarnings = new EventEmitter<string[]>();
  @Output()
  onClick = new EventEmitter<any>();

  private loadingView: NgbModalRef;
  _disabled = false;

  @Input()
  set disabled(val: boolean) {
    this._disabled = val;
  }

  constructor(
    private modalService: NgbModal,
    private quoteSessionService: QuoteSessionService,
    private selectedComponentsService: SelectedComponentsService
  ) {}

  generateFloor(): void {
    this.selectedComponentsService.deselectAll();
    this.onClick.emit();
    this.loadingView = this.modalService.open(LoadingViewComponent, {
      // prevent the user from closing the modal
      backdrop: 'static',
      keyboard: false,
    });
    this.loadingView.componentInstance.progressMessage = 'Generating floor...';
    if (this.clearCutouts || this.clearTransforms) {
      this.quoteSessionService.quoteData
        .pipe(
          first(),
          switchMap((quoteData) => {
            const qd = CloneQuoteData(quoteData);

            if (this.clearCutouts) {
              for (const tierData of qd.FloorData.TiersData.TierData) {
                tierData.CutoutData = [];
              }
            }

            if (this.clearTransforms) {
              qd.TransformData = [];
            }

            // save quote data before generating floor
            this.quoteSessionService.setQuoteData(qd, undefined, true);
            return this.quoteSessionService.onSaved;
          }),
          first()
        )
        .subscribe(() =>
          this.quoteSessionService.runJob(
            'generateFloor',
            null,
            this._onSuccess.bind(this),
            this._onErrors.bind(this),
            this._onWarnings.bind(this)
          )
        );
    } else {
      // nothing to save, continue...
      this.quoteSessionService.runJob(
        'generateFloor',
        null,
        this._onSuccess.bind(this),
        this._onErrors.bind(this),
        this._onWarnings.bind(this)
      );
    }
  }

  private _onSuccess(data: any): void {
    this.loadingView.close();
    this.quoteSessionService.setDrawing(data.drawing);
    if (data.quoteData) {
      this.quoteSessionService.quoteData
        .pipe(first())
        .subscribe((quoteData: QuoteData) => {
          this.quoteSessionService.setQuoteData(data.quoteData, false);
        });
    }
    if (data.backsheet) {
      this.quoteSessionService.setBacksheet(data.backsheet);
    }
    if (!this.hideInfoMessage && data.infoMessage) {
      this.displayInfoMessage(data.infoMessage);
    }
    this.onSuccess.emit(data);
  }

  private _onErrors(errors: string[]): void {
    this.loadingView.close();
    const errorView = this.modalService.open(ErrorModalComponent);
    errorView.componentInstance.errorMessage = errors.toString();
    this.onErrors.emit(errors);
  }

  private _onWarnings(warnings: string[]): void {
    this.loadingView.close();
    const warningView = this.modalService.open(InfoModalComponent);
    warningView.componentInstance.infoMessage = warnings.toString();
    this.onWarnings.emit(warnings);
  }

  private displayInfoMessage(message: string): void {
    const infoView = this.modalService.open(InfoModalComponent);
    infoView.componentInstance.infoMessage = message;
  }
}
